import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Box,
    useTheme,
} from '@mui/material';
import { useSettings } from '../providers/settings-provider';
import Dropdown from './ui/dropdown';

export interface ProfitLossRow {
    label: string;
    fy2025: number;
    fy2026: number;
    isSection?: boolean;
    isTotal?: boolean;
    isBold?: boolean;
    color?: 'income' | 'expense' | 'total';
}

interface ProfitLossTableProps {
    data: ProfitLossRow[];
}

const ProfitLossTable: React.FC<ProfitLossTableProps> = ({ data }) => {
    const theme = useTheme();
    const { formatAmount } = useSettings();
    const [yearRange, setYearRange] = React.useState("2025 - 2026");


    const calculateVariance = (fy2025: number, fy2026: number) => {
        return fy2026 - fy2025;
    };

    const calculateVariancePercent = (fy2025: number, fy2026: number) => {
        if (fy2025 === 0) return 0;
        return ((fy2026 - fy2025) / Math.abs(fy2025)) * 100;
    };

    const getVarianceColor = (variance: number) => {
        return variance < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)';
    };

    // Helper to get row index among non-section, non-total rows for alternating colors
    const getRowIndexForColoring = (globalIndex: number) => {
        return data.filter((_, i) => i < globalIndex && !data[i].isSection && !data[i].isTotal).length;
    };

    // Get all total row indices and find current total row number
    const totalRowIndices = data.map((row, idx) => row.isTotal ? idx : null).filter((idx) => idx !== null);
    
    const getTotalRowNumber = (globalIndex: number) => {
        return totalRowIndices.indexOf(globalIndex);
    };

    return (
        <TableContainer component={Paper} sx={{ borderRadius: 0, padding: 4 }}>
            <Table sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                <TableHead sx={{ backgroundColor: 'var(--color-container-background)' }}>
                    <TableRow>
                        <TableCell
                            style={{
                                fontWeight: 600,
                                fontSize: 16,
                                padding: 12,
                                backgroundColor: 'var(--color-container-background)',
                                border: 'none',
                                borderRadius: '8px 0 0 8px',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography 
                                    sx={{ 
                                        fontWeight: 600,
                                        fontSize: 16, 
                                        color: theme.palette.primary.main
                                    }}>FY</Typography>
                                <Dropdown
                                    options={[
                                        { value: '2025 - 2026', label: '2025 - 2026' },
                                        { value: '2024 - 2025', label: '2024 - 2025' },
                                        { value: '2023 - 2024', label: '2023 - 2024' },
                                    ]}
                                    value={yearRange}
                                    onChange={setYearRange}
                                    width="150px"
                                    height={24}
                                    borderRadius={1}
                                    isPrimary
                                />
                            </Box>
                        </TableCell>
                        <TableCell
                            style={{
                                fontWeight: 600,
                                fontSize: 16,
                                padding: 12,
                                backgroundColor: 'var(--color-container-background)',
                                border: 'none',
                            }}
                            align="right"
                        >
                            FY 2025
                        </TableCell>
                        <TableCell
                            style={{
                                fontWeight: 600,
                                fontSize: 16,
                                padding: 12,
                                backgroundColor: 'var(--color-container-background)',
                                border: 'none',
                            }}
                            align="right"
                        >
                            FY 2026
                        </TableCell>
                        <TableCell
                            style={{
                                fontWeight: 600,
                                fontSize: 16,
                                padding: 12,
                                backgroundColor: 'var(--color-container-background)',
                                border: 'none',
                            }}
                            align="right"
                        >
                            $ FAV / UNFAV
                        </TableCell>
                        <TableCell
                            style={{
                                fontWeight: 600,
                                fontSize: 16,
                                padding: 12,
                                backgroundColor: 'var(--color-container-background)',
                                border: 'none',
                                borderRadius: '0 8px 8px 0',
                            }}
                            align="right"
                        >
                            % FAV / UNFAV
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, idx) => {
                        const variance = calculateVariance(row.fy2025, row.fy2026);
                        const variancePercent = calculateVariancePercent(row.fy2025, row.fy2026);

                        const varianceColor = getVarianceColor(variance);

                        // Determine background color
                        let backgroundColor = 'var(--color-page-background)';
                        if (row.isSection) {
                            // DO NOTHING - keep section rows white
                        } else if (row.isTotal) {
                            // Gradually increase opacity for each total row
                            const totalRowNum = getTotalRowNumber(idx);
                            const opacityLevels = ['26', '4d', '73']; // 15%, 30%, 45% opacity
                            const opacity = opacityLevels[totalRowNum] || opacityLevels[opacityLevels.length - 1];
                            backgroundColor = `${theme.palette.primary.main}${opacity}`;
                        } else {
                            const rowIndex = getRowIndexForColoring(idx);
                            backgroundColor = rowIndex % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)';
                        }

                        return (
                            <TableRow
                                key={idx}
                                sx={{
                                    backgroundColor: backgroundColor,
                                    height: 32,
                                    fontWeight: row.isTotal ? 600 : 400,
                                    fontSize: row.isSection ? 14 : 12,
                                }}
                            >
                                {/* Description Column */}
                                <TableCell
                                    sx={{
                                        p: `${row.isSection ? 4 : 8}px ${(row.isSection || row.isTotal) ? 0 : 16}px`,
                                        fontSize: row.isSection ? 14 : 12,
                                        border: 'none',
                                        whiteSpace: 'normal',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: row.isTotal ? 700 : row.isSection ? 600 : 400,
                                            color: row.isSection ? 'var(--color-primary)' : 'var(--color-default-text)',
                                            pl: 1.25,
                                            fontSize: row.isSection ? 14 : 12,
                                        }}
                                    >
                                        {row.label}
                                    </Typography>
                                </TableCell>

                                {/* FY 2025 Column */}
                                <TableCell
                                    align="right"
                                    sx={{
                                        p: '4px 0',
                                        fontSize: 12,
                                        border: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!row.isSection && (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" pr={2}>
                                            <Typography
                                                sx={{
                                                    fontWeight: row.isTotal ? 700 : 400,
                                                    fontSize: 12,
                                                    color: row.fy2025 < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)',
                                                }}
                                            >
                                                {row.fy2025 < 0 ? `(${formatAmount?.(Math.abs(row.fy2025))})` : formatAmount?.(row.fy2025)}
                                            </Typography>
                                        </Box>
                                    )}
                                </TableCell>

                                {/* FY 2026 Column */}
                                <TableCell
                                    align="right"
                                    sx={{
                                        p: '4px 0',
                                        fontSize: 12,
                                        border: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!row.isSection && (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" pr={2}>
                                            <Typography
                                                sx={{
                                                    fontWeight: row.isTotal ? 700 : 400,
                                                    fontSize: 12,
                                                    color: row.fy2026 < 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)',
                                                }}
                                            >
                                                {row.fy2026 < 0 ? `(${formatAmount?.(Math.abs(row.fy2026))})` : formatAmount?.(row.fy2026)}
                                            </Typography>
                                        </Box>
                                    )}
                                </TableCell>

                                {/* $ FAV / UNFAV Column */}
                                <TableCell
                                    align="right"
                                    sx={{
                                        p: '4px 0',
                                        fontSize: 12,
                                        border: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!row.isSection && (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" pr={2}>
                                            <Typography
                                                sx={{
                                                    fontWeight: row.isTotal ? 700 : 400,
                                                    color: varianceColor,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {variance >= 0 ? (
                                                    formatAmount?.(variance)
                                                ) : (
                                                    `(${formatAmount?.(Math.abs(variance))})`
                                                )}
                                            </Typography>
                                        </Box>
                                    )}
                                </TableCell>

                                {/* % FAV / UNFAV Column */}
                                <TableCell
                                    align="right"
                                    sx={{
                                        p: '4px 0',
                                        fontSize: 12,
                                        border: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!row.isSection && (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" pr={2}>
                                            <Typography
                                                sx={{
                                                    fontWeight: row.isTotal ? 700 : 400,
                                                    color: varianceColor,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {variancePercent >= 0 ? (
                                                    `${variancePercent.toFixed(1)} %`
                                                ) : (
                                                    `(${Math.abs(variancePercent).toFixed(1)} %)`
                                                )}
                                            </Typography>
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProfitLossTable;
